/**
 * Tooltip component — creates floating tooltip elements and attaches
 * hover/click behaviour to elements with a `data-tooltip` attribute.
 */
const luxTooltips = {
    /**
     * Create a floating tooltip DOM element with the given text.
     *
     * @param {string} text - The tooltip content (may contain HTML).
     * @returns {HTMLDivElement} The created tooltip element, already appended to the body.
     */
    createTooltipElement(text) {
        const tooltip = document.createElement("div");
        tooltip.className = "lux-tooltip";
        tooltip.innerHTML = `${text}<div class="lux-tooltip-arrow"></div>`;
        document.body.appendChild(tooltip);
        return tooltip;
    },

    /**
     * Attach hover/click tooltip behaviour to a target element.
     *
     * @param {HTMLElement} target - The element to attach tooltip behaviour to.
     * @returns {void}
     */
    attachTooltip(target) {
        const text = target.getAttribute("data-tooltip");
        if (!text) return;

        /** @type {HTMLDivElement | null} */
        let tooltip = null;

        const updatePosition = () => {
            if (!tooltip) return;

            const rect = target.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();

            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const placeAbove =
                spaceBelow < tooltipRect.height + 16 &&
                spaceAbove > tooltipRect.height + 16;

            let left =
                rect.left +
                window.pageXOffset +
                rect.width / 2 -
                tooltipRect.width / 2;
            let top = placeAbove
                ? rect.top + window.pageYOffset - tooltipRect.height - 8
                : rect.bottom + window.pageYOffset + 8;

            let shift = 0;
            if (left + tooltipRect.width > window.innerWidth - 8) {
                shift = left + tooltipRect.width - (window.innerWidth - 8);
                left -= shift;
            } else if (left < 8) {
                shift = 8 - left;
                left += shift;
            }

            tooltip.style.left = `${left}px`;
            tooltip.style.top = `${top}px`;

            const arrow = /** @type {HTMLElement | null} */ (
                tooltip.querySelector(".lux-tooltip-arrow")
            );
            if (arrow) {
                arrow.style.left = `calc(50% + ${shift}px - 6px)`;
                arrow.classList.toggle("down", placeAbove);
                arrow.classList.toggle("up", !placeAbove);
            }
        };

        const showTooltip = () => {
            if (!tooltip) {
                tooltip = this.createTooltipElement(text);
                tooltip.classList.add("show");
                updatePosition();
            }
        };

        const hideTooltip = () => {
            if (tooltip) {
                tooltip.remove();
                tooltip = null;
            }
            document.removeEventListener("click", outsideClickListener);
        };

        /**
         * Close the tooltip when a click occurs outside of it.
         *
         * @param {MouseEvent} event - The click event to check outside tooltip boundaries.
         */
        const outsideClickListener = (event) => {
            if (
                tooltip &&
                !tooltip.contains(/** @type {Node | null} */ (event.target)) &&
                event.target !== target
            ) {
                hideTooltip();
            }
        };

        if ("ontouchstart" in window) {
            target.addEventListener("click", (/** @type {Event} */ e) => {
                e.preventDefault();
                if (tooltip) {
                    hideTooltip();
                } else {
                    showTooltip();
                    document.addEventListener("click", outsideClickListener);
                }
            });
        } else {
            target.addEventListener("mouseenter", () => {
                showTooltip();
            });

            target.addEventListener("mouseleave", () => {
                setTimeout(() => {
                    if (tooltip && !target.matches(":hover")) {
                        hideTooltip();
                    }
                }, 100);
            });
        }

        window.addEventListener("scroll", hideTooltip, { passive: true });
        window.addEventListener("resize", hideTooltip);
    },

    /**
     * Initialise tooltips on all elements with a `data-tooltip` attribute.
     *
     * @returns {void}
     */
    init() {
        document.querySelectorAll("[data-tooltip]").forEach((el) => {
            this.attachTooltip(/** @type {HTMLElement} */ (el));
        });
    },
};

window.luxTooltips = luxTooltips;

document.addEventListener("DOMContentLoaded", () => {
    window.luxTooltips.init();
});
